import { z } from 'zod';
import { AuditMeta, DocId, IsoTimestamp, TenantId } from '../common.js';
/**
 * Melding informatiebeveiligingsincident — vervangt het Microsoft
 * Forms-formulier "Melden (mogelijk) datalek".
 *
 * Firestore path: security_incidents/{id}
 * Root (niet tenant-subcollectie) omdat suppohrt_admin cross-tenant in één
 * wachtrij beoordeelt, zelfde motivering als change_requests. Elk document
 * stempelt zijn eigen tenantId + submittedByUserId server-side.
 *
 * Kernbesluit (ADR 0013): **datalek is een kwalificatie, geen soort melding**.
 * De melder meldt dat er iets misging; `isDatalek` wordt door de
 * privacy-contactpersoon bij triage gezet. De wettelijke 72-uursklok richting
 * de Autoriteit Persoonsgegevens hangt aan die kwalificatie, niet aan
 * binnenkomst — anders staat het beheerscherm vol rode countdowns voor
 * phishingmails waar niets mee is.
 */
/**
 * Datum zonder tijd (YYYY-MM-DD). De melder kiest een dag in een
 * date-picker; een tijdstip zou schijnprecisie zijn bij een gebeurtenis die
 * vaak achteraf wordt gereconstrueerd. De 72-uursklok rekent vanaf 00:00 van
 * `datumOntdekt` in NL-tijd, dus in het voordeel van de melder.
 */
export const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Verwacht formaat: JJJJ-MM-DD');
/**
 * Vraag 6 uit het formulier. `geen_of_onbekend` is de enige toevoeging ten
 * opzichte van Forms: de intake vangt óók incidenten zonder datalek op (een
 * phishingmail waar niemand op klikte), en die zouden zonder deze optie niet
 * te versturen zijn omdat de vraag verplicht is.
 */
export const IncidentGroep = z.enum(['patient', 'medewerker', 'anders', 'geen_of_onbekend']);
/**
 * Vraag 7. Gecureerde enum conform CLAUDE.md ("curated enums over free-form
 * strings"); een categorie toevoegen is een codewijziging, met opzet.
 * `administratief` dekt paspoort-/ID-nummer, BSN, rekeningnummer en loon —
 * in Forms staat daar "ISBN", vrijwel zeker een typefout voor BSN.
 */
export const IncidentGegevenssoort = z.enum([
    'gezondheid',
    'politiek_levensbeschouwelijk',
    'seksueel_gedrag_gerichtheid',
    'etnische_afkomst',
    'administratief',
    'geen_of_onbekend',
]);
export const IncidentStatus = z.enum(['nieuw', 'in_behandeling', 'afgerond']);
export const IncidentErnst = z.enum(['laag', 'midden', 'hoog']);
/** Handmatig gemeld via het formulier, of aangemaakt uit een log-signaal. */
export const IncidentBron = z.enum(['kennishub', 'log_signaal']);
/**
 * Een gekoppelde audit-logregel, vastgelegd bij de melding zodat de
 * feitenbasis van een AP-melding niet in een los inzagescherm blijft hangen.
 */
export const IncidentLogKoppeling = z.object({
    auditLogId: DocId,
    action: z.string().max(60),
    at: IsoTimestamp,
    toelichting: z.string().max(500).optional(),
    gekoppeldDoorUserId: DocId,
    gekoppeldOp: IsoTimestamp,
});
/**
 * De elf antwoorden van de melder, 1-op-1 uit Forms. Blijft ongewijzigd
 * bewaard als de behandelaar bij triage tot een ander oordeel komt: bij een
 * AP-melding moet te reconstrueren zijn wat de melder wist op het moment van
 * melden.
 */
export const IncidentMelderOpgave = z.object({
    /** Vraag 1. "anoniem" is een geldig antwoord. */
    melderNaam: z.string().min(1).max(200),
    melderContactOptIn: z.boolean().default(false),
    /** Vraag 2. */
    ontdekkingsWijze: z.string().max(5000).optional(),
    /** Vraag 3. Vrije tekst; PII-controle waarschuwt hier, blokkeert niet (plan §5). */
    omschrijving: z.string().max(5000).optional(),
    /** Vraag 4. */
    datumLek: IsoDate,
    /** Vraag 5. Nooit vóór datumLek — afgedwongen op de input. */
    datumOntdekt: IsoDate,
    /** Vraag 6. */
    groepen: z.array(IncidentGroep).min(1).max(4),
    groepenAnders: z.string().max(500).optional(),
    /** Vraag 7. */
    gegevenssoorten: z.array(IncidentGegevenssoort).min(1).max(6),
    /** Vraag 8. */
    andereOrganisaties: z.boolean(),
    /** Vraag 9. Verplicht zodra vraag 8 "ja" is. */
    organisatiesWelke: z.string().max(1000).optional(),
    /** Vraag 10. */
    ondernomenActie: z.string().min(1).max(5000),
    /** Vraag 11. */
    voorgesteldeMaatregelen: z.string().max(5000).optional(),
});
/**
 * Een afgesproken maatregel met eigenaar en einddatum. Zonder die twee is een
 * maatregel een voornemen: bij de evaluatie van een incident is "wie doet dit
 * en wanneer is het klaar" precies wat een auditor navraagt.
 *
 * `wie` is vrije tekst en geen userId, want de eigenaar is vaak iemand buiten
 * de Kennishub (de IT-leverancier, de praktijkmanager, de huisarts zelf).
 */
export const IncidentMaatregel = z.object({
    omschrijving: z.string().min(1).max(2000),
    wie: z.string().min(1).max(200),
    deadline: IsoDate,
    afgerondOp: IsoDate.optional(),
});
/**
 * Het oordeel van de privacy-contactpersoon. Leeg zolang er geen triage is
 * geweest; `isDatalek` blijft dan `null` in plaats van `false`, want "nog
 * niet beoordeeld" en "beoordeeld, geen datalek" zijn verschillende dingen.
 */
export const IncidentTriage = z.object({
    /**
     * Korte titel van wat er gebeurde, door de behandelaar geformuleerd. De
     * omschrijving van de melder is vrije tekst die met PII kan zitten en niet
     * per se leesbaar is in een lijst; de wachtrij, het register en een
     * eventuele AP-melding hebben één regel nodig die de zaak benoemt.
     */
    titel: z.string().max(200).optional(),
    isDatalek: z.boolean().nullable().default(null),
    ernst: IncidentErnst.optional(),
    /** Correctie op vraag 6/7 door de behandelaar; melderopgave blijft staan. */
    groepen: z.array(IncidentGroep).max(4).optional(),
    gegevenssoorten: z.array(IncidentGegevenssoort).max(6).optional(),
    apMeldingNodig: z.boolean().optional(),
    apMeldingMotivering: z.string().max(2000).optional(),
    apGemeldOp: IsoTimestamp.optional(),
    betrokkenenGeinformeerd: z.boolean().optional(),
    behandelaarNotitie: z.string().max(5000).optional(),
    /** Wat er nu gebeurt om de schade te beperken. */
    korteTermijn: IncidentMaatregel.optional(),
    /** Wat er structureel verandert zodat dit niet opnieuw gebeurt. */
    langeTermijn: IncidentMaatregel.optional(),
});
export const SecurityIncidentSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    /**
     * Altijd server-side gestempeld, óók als de melder bij vraag 1 "anoniem"
     * invulde — de audit trail heeft een actor nodig. De UI toont dit veld dan
     * niet. "Anoniem" is dus anoniem voor de behandelaar, niet onherleidbaar;
     * zie besluit 3 in het plan.
     */
    submittedByUserId: DocId,
    /**
     * Kort, uitspreekbaar kenmerk voor aan de telefoon en in de DM: MELD-XXXXXX,
     * afgeleid van de doc-id. Geen teller, want een teller heeft een
     * schrijfconflict nodig dat dit volume niet rechtvaardigt; de id is al uniek.
     */
    meldingsnummer: z.string().max(20).optional(),
    bron: IncidentBron.default('kennishub'),
    /** Gevuld als bron = log_signaal. */
    signaalId: DocId.optional(),
    melderOpgave: IncidentMelderOpgave,
    triage: IncidentTriage.optional(),
    status: IncidentStatus.default('nieuw'),
    behandelaarUserId: DocId.optional(),
    getrieerdOp: IsoTimestamp.optional(),
    afgerondOp: IsoTimestamp.optional(),
    logKoppelingen: z.array(IncidentLogKoppeling).max(50).default([]),
    /**
     * Idempotentie-markers voor de deadline-janitor: welke herinneringen al
     * verstuurd zijn (`triage_24u`, `ap_24u`, `ap_48u`, `ap_72u`). Zonder deze
     * markers stuurt een janitor die elk uur draait elk uur dezelfde DM.
     */
    herinneringenVerstuurd: z.array(z.string().max(40)).max(10).default([]),
});
/**
 * Wat de browser post. Tenant + user worden door de route ingevuld.
 * Twee regels die het formulier zelf ook toont, hier afgedwongen zodat een
 * gemanipuleerde request ze niet omzeilt.
 */
export const SubmitSecurityIncidentInput = IncidentMelderOpgave.extend({
    bron: IncidentBron.optional(),
    signaalId: DocId.optional(),
})
    .refine((v) => v.datumOntdekt >= v.datumLek, {
    message: 'De ontdekkingsdatum kan niet vóór de datum van het incident liggen.',
    path: ['datumOntdekt'],
})
    .refine((v) => !v.andereOrganisaties || !!v.organisatiesWelke?.trim(), {
    message: 'Geef aan welke andere organisaties betrokken zijn.',
    path: ['organisatiesWelke'],
});
export const TriageSecurityIncidentInput = z.object({
    titel: z.string().max(200).optional(),
    isDatalek: z.boolean().nullable(),
    ernst: IncidentErnst.optional(),
    groepen: z.array(IncidentGroep).max(4).optional(),
    gegevenssoorten: z.array(IncidentGegevenssoort).max(6).optional(),
    apMeldingNodig: z.boolean().optional(),
    apMeldingMotivering: z.string().max(2000).optional(),
    apGemeldOp: IsoTimestamp.optional(),
    betrokkenenGeinformeerd: z.boolean().optional(),
    behandelaarNotitie: z.string().max(5000).optional(),
    korteTermijn: IncidentMaatregel.optional(),
    langeTermijn: IncidentMaatregel.optional(),
    status: IncidentStatus.optional(),
});
export const LinkAuditLogInput = z.object({
    auditLogIds: z.array(DocId).min(1).max(25),
    toelichting: z.string().max(500).optional(),
});
/**
 * Detectieregels van de signaal-janitor. Twee regels uit het conceptplan zijn
 * geschrapt omdat de data ze niet draagt: `login.failure` bevat geen
 * Entra-side mislukte logins (die bereiken onze backend nooit) en
 * `user.role_changed` vuurt bij élke login waarbij de groepsafleiding een
 * andere rol oplevert. Wat hier staat, staat aantoonbaar in audit_log.
 */
export const IncidentSignalRule = z.enum([
    'herhaalde_weigering', // >= 3x access.refused, zelfde actor, 24u
    'geweigerde_inlogpogingen', // >= 5x login.failure, zelfde entraOid, 15 min
    'bulk_download', // > 20x document.downloaded, zelfde gebruiker, 1u
    'download_in_de_nacht', // document.downloaded tussen 00:00 en 06:00 NL-tijd
]);
export const IncidentSignalStatus = z.enum(['open', 'melding_gemaakt', 'geen_incident']);
/**
 * Firestore path: security_incident_signals/{id} (root, admin-only).
 *
 * `dedupeKey` = regel + actor + dagvenster, zodat dezelfde reeks niet elk uur
 * opnieuw alarmeert.
 */
export const IncidentSignalSchema = AuditMeta.extend({
    id: DocId,
    tenantId: TenantId,
    rule: IncidentSignalRule,
    dedupeKey: z.string().min(1).max(200),
    /** Wie het gedrag vertoonde. Ontbreekt als de logregels geen actor dragen. */
    actorUserId: DocId.optional(),
    actorEntraOid: z.string().max(100).optional(),
    vensterVan: IsoTimestamp,
    vensterTot: IsoTimestamp,
    aantal: z.number().int().min(1),
    /** De regels waarop het signaal steunt; koppelbaar aan een melding. */
    auditLogIds: z.array(DocId).max(50).default([]),
    status: IncidentSignalStatus.default('open'),
    /** Verplicht bij "geen incident": ook een afwijzing is aantoonbare opvolging. */
    beoordelingMotivering: z.string().max(2000).optional(),
    beoordeeldDoorUserId: DocId.optional(),
    beoordeeldOp: IsoTimestamp.optional(),
    incidentId: DocId.optional(),
});
export const ReviewIncidentSignalInput = z
    .object({
    action: z.enum(['maak_melding', 'geen_incident']),
    motivering: z.string().max(2000).optional(),
})
    .refine((v) => v.action !== 'geen_incident' || !!v.motivering?.trim(), {
    message: 'Geef een motivering waarom dit geen incident is.',
    path: ['motivering'],
});
//# sourceMappingURL=security-incident.js.map